import React from 'react';
import { useNavigate } from 'react-router-dom';

const AdminSideNav = ({ 
  sidebarOpen, 
  setSidebarOpen,
  openMenus,
  setOpenMenus,
  darkMode,
  user,
  colors,
  navigationItems,
  handleLogout,
  toggleDarkMode,
  isMobile,
  isTablet
}) => {
  const navigate = useNavigate();

  const isActivePath = (path) => window.location.pathname === path;

  const isActiveItem = (item) => {
    if (item.path) return isActivePath(item.path);
    if (item.children) {
      return item.children.some(child => isActiveItem(child));
    }
    return false;
  };

  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
      if (isMobile) {
        setSidebarOpen(false);
      }
    }
  };

  const toggleMenu = (label) => {
    setOpenMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const renderNavItem = (item, level = 0) => {
    const isActive = isActiveItem(item);
    const hasChildren = item.children && item.children.length > 0;
    const isOpen = openMenus[item.label];

    // Collapsed sidebar mode (desktop only)
    if (!sidebarOpen && !isMobile) {
      if (level > 0) return null;
      
      return (
        <div
          key={item.label}
          style={{
            position: 'relative',
            marginBottom: '6px',
            width: '100%',
            display: 'flex',
            justifyContent: 'center'
          }}
          title={item.label}
        >
          <button
            onClick={() => hasChildren ? toggleMenu(item.label) : handleNavigation(item.path)}
            style={{
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              border: 'none',
              background: isActive ? colors.primary : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '20px',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.background = colors.lightGray;
                e.currentTarget.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}
          >
            {/* Icon container - keeps icon visible */}
            <span style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {item.icon}
            </span>
            
            {/* Active indicator bar */}
            {isActive && (
              <div style={{
                position: 'absolute',
                left: '0',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '4px',
                height: '24px',
                background: colors.accent,
                borderRadius: '0 4px 4px 0'
              }} />
            )}
          </button>
        </div>
      );
    }

    // Full sidebar mode - Parent menu with children
    if (hasChildren) {
      return (
        <div key={item.label} style={{ marginBottom: '4px' }}>
          <button
            onClick={() => toggleMenu(item.label)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              borderRadius: '12px',
              transition: 'all 0.2s ease',
              width: '100%',
              textAlign: 'left',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              background: isActive ? `${colors.primary}12` : 'transparent',
              color: isActive ? colors.primary : colors.textDark,
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.currentTarget.style.background = isActive ? `${colors.primary}18` : colors.lightGray;
                e.currentTarget.style.transform = 'translateX(4px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isMobile) {
                e.currentTarget.style.background = isActive ? `${colors.primary}12` : 'transparent';
                e.currentTarget.style.transform = 'translateX(0)';
              }
            }}
          >
            {/* Icon with background */}
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: isActive ? `${colors.primary}20` : colors.lightGray,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              flexShrink: 0
            }}>
              {item.icon}
            </div>
            
            <span style={{ flex: 1 }}>{item.label}</span>
            
            {/* Arrow indicator */}
            <span style={{
              fontSize: '10px',
              transition: 'transform 0.25s ease',
              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              color: isActive ? colors.primary : colors.textLight,
              fontWeight: '700'
            }}>
              ▶
            </span>
          </button>
          
          {/* Children dropdown */}
          <div style={{
            maxHeight: isOpen ? '800px' : '0',
            overflow: 'hidden',
            transition: 'max-height 0.3s ease'
          }}>
            <div style={{ 
              paddingLeft: '10px',
              borderLeft: `2px solid ${colors.mediumGray}`,
              marginLeft: '28px',
              marginTop: '6px',
              paddingTop: '4px',
              paddingBottom: '4px'
            }}>
              {item.children.map(child => renderNavItem(child, level + 1))}
            </div>
          </div>
        </div>
      );
    }

    // Regular menu item (no children)
    return (
      <button
        key={item.label}
        onClick={() => handleNavigation(item.path)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: level > 0 ? '10px 14px' : '12px 16px',
          borderRadius: '12px',
          transition: 'all 0.2s ease',
          width: '100%',
          textAlign: 'left',
          border: 'none',
          cursor: 'pointer',
          fontSize: level > 0 ? '13px' : '14px',
          fontWeight: level > 0 ? '500' : '600',
          marginBottom: '4px',
          background: isActive ? colors.primary : 'transparent',
          color: isActive ? colors.white : colors.textDark,
          position: 'relative',
          boxShadow: isActive ? '0 4px 12px rgba(45, 80, 22, 0.25)' : 'none'
        }}
        onMouseEnter={(e) => {
          if (!isActive && !isMobile) {
            e.currentTarget.style.background = colors.lightGray;
            e.currentTarget.style.transform = 'translateX(4px)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive && !isMobile) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.transform = 'translateX(0)';
          }
        }}
      >
        {/* Icon with background - FIXED: icons stay visible */}
        <div style={{
          width: level > 0 ? '28px' : '36px',
          height: level > 0 ? '28px' : '36px',
          borderRadius: level > 0 ? '8px' : '10px',
          background: isActive ? 'rgba(255,255,255,0.2)' : colors.lightGray,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: level > 0 ? '14px' : '18px',
          flexShrink: 0
        }}>
          {item.icon}
        </div>
        
        <div style={{ flex: 1 }}>
          <div>{item.label}</div>
          {item.description && level > 0 && !isMobile && (
            <div style={{ 
              fontSize: '10px', 
              opacity: isActive ? 0.8 : 0.5,
              marginTop: '2px',
              fontWeight: '400'
            }}>
              {item.description}
            </div>
          )}
        </div>
        
        {/* Active indicator dot */}
        {isActive && level === 0 && (
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: colors.white,
            opacity: 0.8
          }} />
        )}
      </button>
    );
  };

  const sidebarWidth = isMobile ? '280px' : (isTablet ? '280px' : '290px');
  const collapsedWidth = '76px';

  const styles = {
    sidebar: {
      position: 'fixed',
      left: isMobile ? (sidebarOpen ? '0' : '-100%') : '0',
      top: 0,
      bottom: 0,
      width: sidebarOpen ? sidebarWidth : collapsedWidth,
      background: colors.white,
      borderRight: `1px solid ${colors.mediumGray}`,
      overflow: 'hidden',
      transition: isMobile 
        ? 'left 0.3s ease' 
        : 'width 0.3s ease',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '2px 0 16px rgba(0, 0, 0, 0.04)',
      zIndex: 1000
    },
    sidebarHeader: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
      color: colors.white,
      padding: sidebarOpen ? '20px 18px' : '20px 14px',
      position: 'relative',
      overflow: 'hidden',
      minHeight: sidebarOpen ? '90px' : '76px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      transition: 'all 0.3s ease'
    },
    headerPattern: {
      position: 'absolute',
      top: '-20px',
      right: '-20px',
      width: '120px',
      height: '120px',
      background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)',
      borderRadius: '50%'
    },
    logo: {
      fontSize: '20px',
      fontWeight: '700',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      position: 'relative',
      zIndex: 1,
      justifyContent: sidebarOpen ? 'flex-start' : 'center'
    },
    logoIcon: {
      fontSize: '26px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    tagline: {
      fontSize: '11px',
      opacity: 0.85,
      fontWeight: '400',
      marginTop: '6px',
      letterSpacing: '0.3px'
    },
    sidebarNav: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: sidebarOpen ? '14px 10px' : '14px 8px',
      scrollbarWidth: 'thin',
      scrollbarColor: `${colors.mediumGray} transparent`
    },
    sidebarFooter: {
      padding: sidebarOpen ? '14px' : '10px',
      borderTop: `1px solid ${colors.mediumGray}`,
      background: colors.lightGray
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '10px 12px',
      marginBottom: sidebarOpen ? '12px' : '0',
      borderRadius: '10px',
      background: colors.white,
      border: `1px solid ${colors.mediumGray}`,
      justifyContent: sidebarOpen ? 'flex-start' : 'center'
    },
    userAvatar: {
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      color: colors.white,
      fontWeight: '700',
      flexShrink: 0
    },
    userName: {
      fontSize: '13px',
      fontWeight: '600',
      color: colors.textDark,
      marginBottom: '1px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    userRole: {
      fontSize: '11px',
      color: colors.textLight,
      fontWeight: '500'
    },
    logoutButton: {
      width: '100%',
      padding: '11px 16px',
      borderRadius: '10px',
      border: 'none',
      background: `linear-gradient(135deg, ${colors.danger}, #dc2626)`,
      color: colors.white,
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(239, 68, 68, 0.25)'
    }
  };

  return (
    <div style={styles.sidebar}>
      {/* Header */}
      <div style={styles.sidebarHeader}>
        <div style={styles.headerPattern}></div>
        <div style={styles.logo}>
          <span style={styles.logoIcon}>🏨</span>
          {sidebarOpen && <span>Learning Hub PMS</span>}
        </div>
        {sidebarOpen && <div style={styles.tagline}>Premium Management System</div>}
      </div>

      {/* Navigation */}
      <div style={styles.sidebarNav}>
        {navigationItems.map(item => renderNavItem(item))}
      </div>

      {/* Footer */}
      <div style={styles.sidebarFooter}>
        <div style={styles.userInfo}>
          <div style={styles.userAvatar}>
            {user.name.charAt(0).toUpperCase()}
          </div>
          {sidebarOpen && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.userName}>{user.name}</div>
              <span style={styles.userRole}>{user.role}</span>
            </div>
          )}
        </div>
        
        {sidebarOpen && (
          <button
            onClick={handleLogout}
            style={styles.logoutButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(239, 68, 68, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.25)';
            }}
          >
            🚪 Logout
          </button>
        )}
        
        {!sidebarOpen && !isMobile && (
          <button
            onClick={handleLogout}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '10px',
              border: 'none',
              background: colors.danger,
              color: colors.white,
              cursor: 'pointer',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '10px auto 0',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="Logout"
          >
            🚪
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminSideNav;